import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import get from 'lodash/get';

import { PCP_NOT_LISTED_VALUE } from '../../../../constants/pcpAnswer';
import { getPCPDictionaries } from '../../../../utils/getPCPDictionaries';

const INITIAL_FORM_STATE = {
  pcpProviderAnswer: null,
  pcpName: '',
  considerUsPcp: null,
  pcp: null,
};

const toOptionsList = dict =>
  Object.entries(dict).map(([value, label]) => ({ label, value }));

const usePCPWithExtraFieldsForm = initialValues => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  const pcpNameFieldRef = useRef(null);

  const focusTimeoutRef = useRef(null);

  const { formatMessage } = useIntl();

  const { considerUsAsYourPCPAnswers, pcpAnswers } = getPCPDictionaries({
    formatMessage,
  });

  const optionsList = useMemo(() => toOptionsList(pcpAnswers), [pcpAnswers]);

  const considerUsAsYourPCPOptions = useMemo(
    () => toOptionsList(considerUsAsYourPCPAnswers),
    [considerUsAsYourPCPAnswers],
  );

  const pcpYes = formData.pcpProviderAnswer === optionsList[0].value;
  const pcpNo = formData.pcpProviderAnswer === optionsList[1].value;

  const isPcpNameNotListedSelected =
    get(formData, 'pcp.value', '') === PCP_NOT_LISTED_VALUE;

  const isNextButtonDisabled = useMemo(() => {
    if (!formData.pcpProviderAnswer) {
      return true;
    }

    if (pcpYes) {
      if (isPcpNameNotListedSelected && !formData.pcpName.trim()) {
        return true;
      }

      if (!formData.pcp) {
        return true;
      }
    }

    if (pcpNo && !formData.considerUsPcp) {
      return true;
    }

    return false;
  }, [formData, isPcpNameNotListedSelected]);

  const showPcpNameField = pcpYes && isPcpNameNotListedSelected;

  const handleOptionChange = useCallback(
    value => () => {
      setFormData(prevState => ({ ...prevState, pcpProviderAnswer: value }));
    },
    [],
  );

  const handlePcpNameChange = useCallback(value => {
    setFormData(prevState => ({ ...prevState, pcpName: value.trim() }));
  }, []);

  const handleConsiderUsAsYourPCPChange = useCallback(item => {
    const selectedValue = get(item, 'value', null);

    setFormData(prevState => ({
      ...prevState,
      considerUsPcp: selectedValue,
    }));
  }, []);

  const handlePcpChange = useCallback(item => {
    const isPcpNotListedOption =
      get(item, 'value', '') === PCP_NOT_LISTED_VALUE;

    setFormData(prevState => ({
      ...prevState,
      pcp: item,
      pcpName: INITIAL_FORM_STATE.pcpName,
    }));

    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }

    if (pcpNameFieldRef.current && isPcpNotListedOption) {
      focusTimeoutRef.current = setTimeout(
        () => pcpNameFieldRef.current.focus(),
        200,
      );
      focusTimeoutRef.current = null;
    }
  }, []);

  const setPcpNameFieldRef = useCallback(node => {
    pcpNameFieldRef.current = node;
  }, []);

  useEffect(() => {
    if (initialValues) {
      setFormData(prevState => ({ ...prevState, ...initialValues }));
    }
  }, [initialValues]);

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  return {
    considerUsAsYourPCPOptions,
    formData,
    handleConsiderUsAsYourPCPChange,
    handleOptionChange,
    handlePcpNameChange,
    handlePcpChange,
    isNextButtonDisabled,
    optionsList,
    pcpNo,
    pcpYes,
    setPcpNameFieldRef,
    showPcpNameField,
  };
};

export default usePCPWithExtraFieldsForm;



// WEBPACK FOOTER //
// ./src/components/forms/PrimaryCareProviderWithExtraFieldsForm/hooks/usePCPWithExtraFieldsForm.js